<template>
  <div class="search-container">
    <div class="search">
      <TextInput
        ref="searchInput"
        v-model="searchQuery"
        icon-before="fas fa-search"
        placeholder="Search by name or email"
        size="3"
        :loading="loading"
      />
    </div>

    <template v-if="hasFetched">
      <MenuList
        v-if="filteredItems.length"
        :class="listClasses"
        :style="{ '--show-count': showCount }"
      >
        <MenuItem
          v-for="item in filteredItems.slice(0, maxItems)"
          :key="item[keyField]"
          type="button"
          :active="isSelected(item)"
          expand-slot
          @click="toggleItem(item[keyField])"
        >
          <div class="search-content">
            <slot
              :item="item"
            >
              <!-- Fallback -->
              {{ item[displayField] }}
            </slot>
            <Icon
              :class="{ 'tw-opacity-0': !isSelected(item) }"
              icon="fas fa-check-circle"
            />
          </div>
        </MenuItem>
      </MenuList>

      <div
        v-else
        class="search-empty"
      >
        <p>No result</p>
        <Button
          size="3_5"
          spacing="tight"
          @click="resetSearch"
        >
          Reset search
        </Button>
      </div>
    </template>

    <div
      v-else
      class="search-empty"
    >
      <LoaderWithText />
    </div>
  </div>
</template>


<script setup>
import {
  ref,
  computed,
  watch,
  defineEmits,
  defineProps,
} from 'vue';

import { Button, Icon, LoaderWithText, MenuItem, MenuList, TextInput } from '@stakeui';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  // Any items that should be selected to start (editing an existing list)
  initSelected: {
    type: Array,
    default: () => [],
  },
  keyField: {
    type: String,
    default: 'id',
  },
  displayField: {
    type: String,
    default: 'name',
  },
  valueField: {
    type: String,
    default: null,
  },
  searchFields: {
    type: Array,
    default: () => ['name'],
  },
  maxItems: {
    type: Number,
    default: 100,
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  hasFetched: {
    type: Boolean,
    default: false,
  },
  showCount: {
    type: Number,
    default: 7.5,
  },
});


// Reactive state
const searchQuery = ref('');
const selected = ref([]);
const showSelectedOnly = ref(false);
const searchInput = ref(null);


// Computed props

const selectedSet = computed(() => {
  return new Set(selected.value);
});


const selectedItems = computed(() => {
  const key = props.keyField;
  return props.items.filter(item => selectedSet.value.has(item[key]));
});


const activeItems = computed(() => {
  if (showSelectedOnly.value) {
    return selectedItems.value;
  }
  return props.items;
});


const filteredItems = computed(() => {
  const query = searchQuery.value.toLowerCase();
  if (!query) return activeItems.value;

  return activeItems.value.filter(item =>
    props.searchFields.some(field => {
      const value = item[field]?.toLowerCase() ?? '';

      if (query.length < 2) {
        return value.startsWith(query);
      }
      return value.includes(query);
    }),
  );
});

const isTruncated = computed(() => props.items.length > props.showCount);

const listClasses = computed(() => [
  'search-list',
  isTruncated.value ? `is-truncated` : '',
]);


// Watchers

watch(searchQuery, newQuery => {
  emit('search', newQuery);
});


watch(selectedSet, newSelectedSet => {
  const data = getReturnData(newSelectedSet);
  emit('selection-change', data);
});


watch(() => props.initSelected,
    newSelected => {
      selected.value = [...newSelected.map(item => {
        if (typeof item === 'object') {
          return item[props.keyField];
        }
        return item;
      })];
    },
    { immediate: true },
);


// Regular methods

const toggleItem = key => {
  if (selectedSet.value.has(key)) {
    selected.value = selected.value.filter(k => k !== key);
    //
  } else if (props.multiple === true) {
    selected.value.push(key);
    //
  } else {
    selected.value = [key];
  }
};


const getSelectedItems = keySet => {
  const key = props.keyField;
  return props.items.filter(item => keySet.has(item[key]));
};


const getReturnData = newSelectedSet => {
  const newSelectedItems = getSelectedItems(newSelectedSet);

  if (props.valueField) {
    if (props.valueField === props.keyField) {
      return selected.value;
    }
    return newSelectedItems.map(item => item[props.valueField]);
  }
  return newSelectedItems;
};

const isSelected = item => {
  return selectedSet.value.has(item[props.keyField]);
};

// Events
const emit = defineEmits(['search', 'selection-change']);

const resetSearch = () => {
  searchQuery.value = '';
  searchInput.value?.focus();
};
</script>


<style scoped>
.search-container {
  --show-count: 7.5;
  --height-of-single-item: 2.25em;
}

.search {
  display: flex;
  margin-bottom: 0.5em;
  position: relative;
}

.search-loader {
  color: var(--stk-text-weak);
  position: absolute;
  right: 0.75em;
  top: 0.75em;
}

.search-list {
  &.is-truncated {
    height: calc(var(--show-count) * var(--height-of-single-item));
    overflow-y: auto;
    border: 1px solid var(--stk-border);
    border-radius: 0.5em 0 0 0.5em;
    padding: 0.5em;
    gap: 2px;
  }
}

.search-content {
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
}

.search-empty {
  background-color: var(--stk-background);
  border: 1px solid var(--stk-border);
  border-radius: 0.5em;
  color: var(--stk-text-weak);
  justify-content: center;
  display: flex;
  height: calc(var(--show-count) * var(--height-of-single-item));
  align-items: center;
  flex-direction: column;
  gap: 0.5em;
}
</style>
